import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  Optional,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { FloatingDialogLayoutService } from '../../core/services/floating-dialog-layout.service';
import { VerticalBox, VisibleArea, fitVertically, visibleArea } from './visible-area';

type ResizeDir = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

/**
 * Adds 8-way resize handles to the host's `.cdk-overlay-pane` and persists bounds to localStorage.
 *
 * The handles are absolutely positioned inside the pane, so the pane has to be `position: fixed`.
 * The CDK's global position strategy writes `position: static` and margins onto the pane on the
 * tick after the content's view initialises, undoing anything set earlier, so in a dialog the
 * pane is pinned and the handles added once the dialog reports it has opened.
 *
 * While a soft keyboard covers the foot of the screen the pane is moved up, and shortened if it must
 * be, so its composer stays in view; it goes back to its own place when the keyboard closes.
 */
@Directive({
  selector: '[appResizableOverlayPane]',
})
export class ResizableOverlayPaneDirective implements AfterViewInit, OnDestroy {
  @Input('appResizableOverlayPane') storageKey = '';

  @Input() resizeMinWidth = 280;
  @Input() resizeMinHeight = 200;

  private pane?: HTMLElement;
  private handles: HTMLElement[] = [];
  private moveListener?: (e: MouseEvent) => void;
  private upListener?: () => void;
  private opened?: Subscription;
  /** Where the pane sits when nothing covers the screen: where it opened, or where it was last resized to. */
  private own?: VerticalBox;
  private readonly fit = (): void => this.fitToVisibleArea();

  constructor(
    private el: ElementRef<HTMLElement>,
    private layout: FloatingDialogLayoutService,
    @Optional() private dialogRef?: MatDialogRef<unknown>
  ) {}

  ngAfterViewInit(): void {
    this.pane = this.el.nativeElement.closest('.cdk-overlay-pane') ?? undefined;
    if (!this.pane || !this.storageKey) {
      return;
    }
    if (this.dialogRef) {
      this.opened = this.dialogRef.afterOpened().subscribe(() => this.setUp());
    } else {
      this.setUp();
    }
  }

  ngOnDestroy(): void {
    this.opened?.unsubscribe();
    this.endResize();
    window.removeEventListener('resize', this.fit);
    window.visualViewport?.removeEventListener('resize', this.fit);
    window.visualViewport?.removeEventListener('scroll', this.fit);
    for (const h of this.handles) {
      h.remove();
    }
    this.handles = [];
  }

  private setUp(): void {
    if (!this.pane) {
      return;
    }
    this.pane.style.boxSizing = 'border-box';
    this.ensureFixedLayout();
    this.createHandles();
    this.keepOwnBox();
    window.addEventListener('resize', this.fit);
    window.visualViewport?.addEventListener('resize', this.fit);
    window.visualViewport?.addEventListener('scroll', this.fit);
    this.fitToVisibleArea();
  }

  private keepOwnBox(): void {
    if (this.pane) {
      const rect = this.pane.getBoundingClientRect();
      this.own = { top: rect.top, height: rect.height };
    }
  }

  private fitToVisibleArea(): void {
    if (!this.pane || !this.own) {
      return;
    }
    const box = fitVertically(this.own, this.visible(), 8);
    this.pane.style.top = `${box.top}px`;
    this.pane.style.height = `${box.height}px`;
  }

  private visible(): VisibleArea {
    return visibleArea();
  }

  private ensureFixedLayout(): void {
    if (!this.pane) {
      return;
    }
    const rect = this.pane.getBoundingClientRect();
    if (this.pane.style.position !== 'fixed') {
      this.pane.style.position = 'fixed';
      this.pane.style.left = `${rect.left}px`;
      this.pane.style.top = `${rect.top}px`;
      this.pane.style.margin = '0';
      this.pane.style.transform = '';
    }
    this.pane.style.width = `${rect.width}px`;
    this.pane.style.height = `${rect.height}px`;
  }

  private createHandles(): void {
    if (!this.pane) {
      return;
    }
    const dirs: ResizeDir[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
    for (const dir of dirs) {
      const h = document.createElement('div');
      h.className = `floating-dialog-resize-handle floating-dialog-resize-${dir}`;
      h.setAttribute('data-resize-dir', dir);
      const cursor = this.cursorFor(dir);
      h.style.cssText = [
        'position:absolute',
        'z-index:1000',
        `cursor:${cursor}`,
        'pointer-events:auto',
        'box-sizing:border-box',
      ].join(';');
      this.setHandleBox(h, dir);
      h.addEventListener('mousedown', (ev) => this.onResizeStart(ev, dir));
      this.pane.appendChild(h);
      this.handles.push(h);
    }
  }

  private setHandleBox(el: HTMLElement, dir: ResizeDir): void {
    const t = 8;
    const c = 12;
    switch (dir) {
      case 'n':
        el.style.left = '0';
        el.style.right = '0';
        el.style.top = '0';
        el.style.height = `${t}px`;
        break;
      case 's':
        el.style.left = '0';
        el.style.right = '0';
        el.style.bottom = '0';
        el.style.height = `${t}px`;
        break;
      case 'e':
        el.style.top = '0';
        el.style.bottom = '0';
        el.style.right = '0';
        el.style.width = `${t}px`;
        break;
      case 'w':
        el.style.top = '0';
        el.style.bottom = '0';
        el.style.left = '0';
        el.style.width = `${t}px`;
        break;
      case 'ne':
        el.style.top = '0';
        el.style.right = '0';
        el.style.width = `${c}px`;
        el.style.height = `${c}px`;
        break;
      case 'nw':
        el.style.top = '0';
        el.style.left = '0';
        el.style.width = `${c}px`;
        el.style.height = `${c}px`;
        break;
      case 'se':
        el.style.bottom = '0';
        el.style.right = '0';
        el.style.width = `${c}px`;
        el.style.height = `${c}px`;
        break;
      case 'sw':
        el.style.bottom = '0';
        el.style.left = '0';
        el.style.width = `${c}px`;
        el.style.height = `${c}px`;
        break;
    }
  }

  private cursorFor(dir: ResizeDir): string {
    const m: Record<ResizeDir, string> = {
      n: 'ns-resize',
      s: 'ns-resize',
      e: 'ew-resize',
      w: 'ew-resize',
      ne: 'nesw-resize',
      nw: 'nwse-resize',
      se: 'nwse-resize',
      sw: 'nesw-resize',
    };
    return m[dir];
  }

  private onResizeStart(event: MouseEvent, dir: ResizeDir): void {
    if (event.button !== 0 || !this.pane) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startY = event.clientY;
    const rect = this.pane.getBoundingClientRect();
    let left = rect.left;
    let top = rect.top;
    let width = rect.width;
    let height = rect.height;

    this.moveListener = (ev: MouseEvent) => {
      if (!this.pane) {
        return;
      }
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const minW = this.resizeMinWidth;
      const minH = this.resizeMinHeight;

      let nl = left;
      let nt = top;
      let nw = width;
      let nh = height;

      if (dir.includes('e')) {
        nw = width + dx;
      }
      if (dir.includes('w')) {
        nl = left + dx;
        nw = width - dx;
      }
      if (dir.includes('s')) {
        nh = height + dy;
      }
      if (dir.includes('n')) {
        nt = top + dy;
        nh = height - dy;
      }

      if (nw < minW) {
        if (dir.includes('w')) {
          nl = left + width - minW;
        }
        nw = minW;
      }
      if (nh < minH) {
        if (dir.includes('n')) {
          nt = top + height - minH;
        }
        nh = minH;
      }

      const margin = 8;
      nw = Math.min(nw, window.innerWidth - margin * 2);
      nh = Math.min(nh, window.innerHeight - margin * 2);
      nl = Math.max(margin, Math.min(nl, window.innerWidth - nw - margin));
      nt = Math.max(margin, Math.min(nt, window.innerHeight - nh - margin));
      if (nl + nw > window.innerWidth - margin) {
        nw = window.innerWidth - margin - nl;
      }
      if (nt + nh > window.innerHeight - margin) {
        nh = window.innerHeight - margin - nt;
      }
      nw = Math.max(nw, minW);
      nh = Math.max(nh, minH);

      this.pane.style.position = 'fixed';
      this.pane.style.left = `${nl}px`;
      this.pane.style.top = `${nt}px`;
      this.pane.style.width = `${nw}px`;
      this.pane.style.height = `${nh}px`;
      this.pane.style.margin = '0';
      this.pane.style.transform = '';
    };

    this.upListener = () => {
      if (this.pane && this.storageKey) {
        this.layout.saveFromPane(this.storageKey, this.pane);
      }
      this.keepOwnBox();
      this.endResize();
    };

    document.addEventListener('mousemove', this.moveListener);
    document.addEventListener('mouseup', this.upListener, { once: true });
  }

  private endResize(): void {
    if (this.moveListener) {
      document.removeEventListener('mousemove', this.moveListener);
      this.moveListener = undefined;
    }
    if (this.upListener) {
      document.removeEventListener('mouseup', this.upListener);
      this.upListener = undefined;
    }
  }
}
